import React, { useMemo, useRef } from 'react';
import { styles } from './CardGrid.css';
import { GridSlot } from './GridSlot';
import { assignGridSlots } from './helpers';
import { Card, CardProps } from '../Card';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { HeroTile } from '../../HeroTile/HeroTile';
import { PossiblyResponsiveValue, useResponsiveValue } from '../../hooks/useResponsiveValue';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';

export enum CardGridVariant {
  default = 'default',
  withPrimary = 'withPrimary',
}

export type CardGridCardProps = CardProps & { isFixedHeight?: boolean };

export type CardGridProps = {
  cards: Array<CardGridCardProps>;
  variant?: CardGridVariant;
  isCarousel?: PossiblyResponsiveValue<boolean>;
  enableContainerBreakpoints?: boolean;
};

export const CardGrid = ({
  cards,
  variant = CardGridVariant.default,
  isCarousel: isCarouselProp,
  enableContainerBreakpoints = false,
}: CardGridProps) => {
  const isDefault = variant === CardGridVariant.default;
  const containerRef = useRef<HTMLDivElement>(null);
  const isCarousel = useResponsiveValue(
    isCarouselProp ?? (isDefault ? { xs: true, lg: false } : { xs: true, md: false }),
    enableContainerBreakpoints ? containerRef : undefined
  );
  const gridSlotData = useMemo(() => assignGridSlots(cards), [cards]);

  if (!cards.length) {
    return null;
  }
  if (isCarousel) {
    return (
      <CardGridCarousel
        cards={cards}
        enableContainerBreakpoints={enableContainerBreakpoints}
        containerRef={enableContainerBreakpoints ? containerRef : undefined}
      />
    );
  }

  return (
    <div className={styles.cardGrid({ variant })} ref={enableContainerBreakpoints ? containerRef : undefined}>
      {cards.map((cardProps, index) => {
        if (isDefault) {
          return <CardGridCard {...cardProps} key={index} />;
        }
        const { isPrimary, slots } = gridSlotData[index];
        return (
          <GridSlot key={index} isPrimary={isPrimary} slots={slots}>
            <CardGridCard
              {...cardProps}
              {...(isPrimary ? { headingSize: 'large' } : !cardProps.isFixedHeight ? { content: null } : {})}
              key={index}
            />
          </GridSlot>
        );
      })}
    </div>
  );
};

const CardGridCarousel = ({
  cards,
  enableContainerBreakpoints,
  containerRef,
}: {
  cards: Array<CardGridCardProps>;
  enableContainerBreakpoints?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}) => {
  const isFullBleed = useBreakpoint('md', 'down');
  const [fixedCards, standardCards] = useMemo(
    () =>
      cards.reduce(
        ([fixed, standard]: Array<Array<CardGridCardProps>>, article) => {
          (article.isFixedHeight ? fixed : standard).push(article);
          return [fixed, standard];
        },
        [[], []]
      ),
    [cards]
  );

  return (
    <div
      className={classNames(styles.carouselWrapper, enableContainerBreakpoints && styles.enableContainerBreakpoints)}
      ref={containerRef}
    >
      <Carousel
        showProgressBar
        spaceBetween={10}
        slidesOffsetAfter={2}
        fullBleed={isFullBleed}
        showPagination={false}
        showArrows={false}
        slidesPerView="auto"
      >
        {standardCards.map((cardProps, index) => (
          <CardGridCard key={`standard-${index}`} {...cardProps} width="345px" />
        ))}
      </Carousel>
      {fixedCards.map((cardProps, index) => (
        <CardGridCard key={`fixed-${index}`} {...cardProps} />
      ))}
    </div>
  );
};

const CardGridCard = ({ isFixedHeight = false, ...cardProps }: CardGridCardProps) => {
  if (isFixedHeight) {
    return <HeroTile {...cardProps} />;
  }
  return <Card {...cardProps} />;
};
